import { useCallback, useEffect, useState } from 'react';
import { Link, useBlocker } from 'react-router-dom';
import PageHeader from '../reusable/PageHeader';
import Panel from '../reusable/Panel';
import CustomButton from '../reusable/CustomButton';
import { NAVIGATION_LINKS_DATA } from '../../constants';
import CourseMappingTable from './CourseMappingTable';
import { COURSE_MAPPING_GROUPS_PAGE_SIZE } from './courseMappingTableUtils';
import CourseMappingFilters from './CourseMappingFilters';
import CourseMappingViewSelector from './CourseMappingViewSelector';
import { COURSE_MAPPING_PRESETS, DEFAULT_PRESET_ID, findPreset } from './courseMappingPresets';
import { buildCourseFilterParams, CourseMappingFilterState, serializeFilterParams } from './courseMappingFilterFields';
import { buildCourseSortParams, CourseMappingSortState, serializeSortParams } from './courseMappingSort';
import { COURSE_MAPPING_COLUMNS } from './courseMappingColumns';
import CourseMappingUnsavedChangesModal from './CourseMappingUnsavedChangesModal';
import useFetchData from '../hooks/useFetchData';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import { fetchCoursesPage, getAcademicPeriods, saveCourseMappingChanges } from '../../services/coursesService';
import {
  getCourseMappingPresetViewStatus,
  markCourseMappingPresetViewed,
  PRESET_NOT_VIEWED_STATE,
} from '../../services/workflowService';
import { getConfiguredSchools, getSubjectAreas } from '../../services/eoaConfigServices';
import { mapCourseSearchResultsToGroups } from '../../utils/mapCourseSearchToCourseMappingGroups';
import { buildSavePayload, CourseMappingPendingChanges, hasPendingChanges } from '../../utils/courseMappingChanges';
import { CourseMappingGroup } from '../../interfaces/CourseMappingInterface';
import { SubjectArea } from '../../interfaces/EOAInterface';

import './CourseMappingPage.css';

const CourseMappingPage = () => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const [pendingChanges, setPendingChanges] = useState<CourseMappingPendingChanges>({});
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [requestedPage, setRequestedPage] = useState<number | null>(null);
  const [filters, setFilters] = useState<CourseMappingFilterState>([]);
  const [requestedFilters, setRequestedFilters] = useState<CourseMappingFilterState | null>(null);
  const [selectedPresetId, setSelectedPresetId] = useState(DEFAULT_PRESET_ID);
  const [requestedPresetId, setRequestedPresetId] = useState<string | null>(null);
  const [unviewedPresetIds, setUnviewedPresetIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<CourseMappingSortState | null>(null);
  const [requestedSort, setRequestedSort] = useState<{ value: CourseMappingSortState | null } | null>(null);
  const [filterToOpen, setFilterToOpen] = useState<{ key: string; nonce: number } | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

  const activePreset = findPreset(selectedPresetId) ?? COURSE_MAPPING_PRESETS[0];

  const {
    isPending,
    error,
    data: { groups: fetchedGroups, totalMatching },
    refreshData,
  } = useFetchData({
    fetchData: async (eoaId, access_token) => {
      const { results, total_matching } = await fetchCoursesPage(eoaId, access_token, {
        limit: COURSE_MAPPING_GROUPS_PAGE_SIZE,
        offset: (currentPage - 1) * COURSE_MAPPING_GROUPS_PAGE_SIZE,
        ...buildCourseFilterParams(filters),
        ...buildCourseSortParams(sort),
      });
      return { data: { groups: mapCourseSearchResultsToGroups(results), totalMatching: total_matching } };
    },
    options: { dependencies: [currentPage, serializeFilterParams(filters), serializeSortParams(sort)] },
    errorMessage: 'Error occurred while fetching course mapping data!',
    initialState: { groups: [] as CourseMappingGroup[], totalMatching: 0 },
  });

  const { data: subjectAreas } = useFetchData({
    fetchData: async (_eoaId, access_token) => {
      const response = await getSubjectAreas(access_token);
      return { data: response.data };
    },
    errorMessage: 'Error occurred while fetching subject areas!',
    initialState: [] as SubjectArea[],
  });

  const { data: schoolNames } = useFetchData({
    fetchData: async (eoaId, access_token) => {
      const response = await getConfiguredSchools(eoaId, access_token);
      return { data: response.data.map((school) => school.name).filter((name): name is string => Boolean(name)) };
    },
    errorMessage: 'Error occurred while fetching schools!',
    initialState: [] as string[],
  });

  const { data: academicPeriods } = useFetchData({
    fetchData: async (eoaId, access_token) => {
      const data = await getAcademicPeriods(eoaId, access_token);
      return { data };
    },
    errorMessage: 'Error occurred while fetching academic periods!',
    initialState: [] as string[],
  });

  const canSave = hasPendingChanges(pendingChanges);
  const navigationBlocker = useBlocker(canSave);
  const isRouteNavigationBlocked = navigationBlocker.state === 'blocked';
  const isUnsavedChangesModalOpen =
    canSave &&
    (requestedPage !== null || requestedFilters !== null || requestedSort !== null || isRouteNavigationBlocked);

  useEffect(() => {
    if (!canSave) {
      setRequestedPage(null);
      setRequestedFilters(null);
      setRequestedPresetId(null);
      setRequestedSort(null);
    }
  }, [canSave]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!canSave) {
        return;
      }

      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [canSave]);

  const eoaId = userDetails?.eoaId;
  const accessToken = userDetails?.access_token;

  useEffect(() => {
    if (!eoaId || !accessToken) {
      return undefined;
    }

    let isActive = true;
    // The default "All courses" is the landing view, so it never shows a beacon.
    const beaconPresets = COURSE_MAPPING_PRESETS.filter((preset) => preset.id !== DEFAULT_PRESET_ID);

    void Promise.all(
      beaconPresets.map(async (preset) => {
        try {
          const status = await getCourseMappingPresetViewStatus(eoaId, preset.id, accessToken);
          return status.current_state === PRESET_NOT_VIEWED_STATE ? preset.id : null;
        } catch {
          return null;
        }
      })
    ).then((results) => {
      if (isActive) {
        setUnviewedPresetIds(new Set(results.filter((id): id is string => id !== null)));
      }
    });

    return () => {
      isActive = false;
    };
  }, [eoaId, accessToken]);

  const markPresetViewedIfNeeded = useCallback(
    (presetId: string) => {
      if (!eoaId || !accessToken || !unviewedPresetIds.has(presetId)) {
        return;
      }

      setUnviewedPresetIds((prev) => {
        const next = new Set(prev);
        next.delete(presetId);
        return next;
      });

      void markCourseMappingPresetViewed(eoaId, presetId, accessToken).catch(() => {
        // Best-effort: a failed mark just means the dot reappears on next load.
      });
    },
    [accessToken, eoaId, unviewedPresetIds]
  );

  const savePendingChanges = useCallback(async (): Promise<boolean> => {
    if (!userDetails || !canSave) {
      return false;
    }

    setIsSaving(true);

    try {
      const updates = buildSavePayload(fetchedGroups, pendingChanges);
      await saveCourseMappingChanges(userDetails.eoaId, userDetails.access_token, updates);
      setPendingChanges({});
      await refreshData();
      return true;
    } catch (saveError) {
      handleOpenErrorModal('Error occurred while saving course mapping changes!', saveError as Error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [canSave, fetchedGroups, handleOpenErrorModal, pendingChanges, refreshData, userDetails]);

  const handleSave = useCallback(() => {
    void savePendingChanges();
  }, [savePendingChanges]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (hasPendingChanges(pendingChanges)) {
        setRequestedPage(page);
        return;
      }

      setCurrentPage(page);
    },
    [pendingChanges]
  );

  const applyFilters = useCallback((next: CourseMappingFilterState) => {
    setFilters(next);
    setCurrentPage(1);
  }, []);

  const applySort = useCallback((next: CourseMappingSortState | null) => {
    setSort(next);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback(
    (next: CourseMappingSortState | null) => {
      const sortChanged = serializeSortParams(next) !== serializeSortParams(sort);

      if (!sortChanged) {
        return;
      }

      if (hasPendingChanges(pendingChanges)) {
        setRequestedSort({ value: next });
        return;
      }

      applySort(next);
    },
    [applySort, pendingChanges, sort]
  );

  const handleOpenFilterForColumn = useCallback((columnKey: string) => {
    setFilterToOpen((prev) => ({ key: columnKey, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  const handleHideColumn = useCallback((columnKey: string) => {
    setHiddenColumns((prev) => {
      // Keep at least one column visible so the reveal markers always have an anchor.
      if (prev.has(columnKey) || prev.size >= COURSE_MAPPING_COLUMNS.length - 1) {
        return prev;
      }

      const next = new Set(prev);
      next.add(columnKey);
      return next;
    });
  }, []);

  const handleShowColumn = useCallback((columnKey: string) => {
    setHiddenColumns((prev) => {
      if (!prev.has(columnKey)) {
        return prev;
      }

      const next = new Set(prev);
      next.delete(columnKey);
      return next;
    });
  }, []);

  const handleFiltersChange = useCallback(
    (next: CourseMappingFilterState) => {
      const appliedParamsChanged = serializeFilterParams(next) !== serializeFilterParams(filters);

      if (!appliedParamsChanged) {
        setFilters(next);
        return;
      }

      if (hasPendingChanges(pendingChanges)) {
        setRequestedFilters(next);
        return;
      }

      applyFilters(next);
    },
    [applyFilters, filters, pendingChanges]
  );

  const handleSelectPreset = useCallback(
    (presetId: string) => {
      const preset = findPreset(presetId);
      if (!preset) {
        return;
      }

      const appliedParamsChanged = serializeFilterParams(preset.filters) !== serializeFilterParams(filters);

      if (appliedParamsChanged && hasPendingChanges(pendingChanges)) {
        setRequestedPresetId(presetId);
        setRequestedFilters(preset.filters);
        return;
      }

      setSelectedPresetId(presetId);
      applyFilters(preset.filters);
      markPresetViewedIfNeeded(presetId);
    },
    [applyFilters, filters, markPresetViewedIfNeeded, pendingChanges]
  );

  const handleCloseUnsavedChangesModal = useCallback(() => {
    setRequestedPage(null);
    setRequestedFilters(null);
    setRequestedPresetId(null);
    setRequestedSort(null);
    if (isRouteNavigationBlocked) {
      navigationBlocker.reset();
    }
  }, [isRouteNavigationBlocked, navigationBlocker]);

  const proceedAfterUnsavedChanges = useCallback(() => {
    const nextPage = requestedPage;
    const nextFilters = requestedFilters;
    const nextPresetId = requestedPresetId;
    const nextSort = requestedSort;
    setRequestedPage(null);
    setRequestedFilters(null);
    setRequestedPresetId(null);
    setRequestedSort(null);

    if (nextFilters !== null) {
      if (nextPresetId !== null) {
        setSelectedPresetId(nextPresetId);
        markPresetViewedIfNeeded(nextPresetId);
      }
      applyFilters(nextFilters);
    } else if (nextSort !== null) {
      applySort(nextSort.value);
    } else if (nextPage !== null) {
      setCurrentPage(nextPage);
    }

    if (isRouteNavigationBlocked) {
      navigationBlocker.proceed();
    }
  }, [
    applyFilters,
    applySort,
    isRouteNavigationBlocked,
    markPresetViewedIfNeeded,
    navigationBlocker,
    requestedFilters,
    requestedPage,
    requestedPresetId,
    requestedSort,
  ]);

  const handleDiscardAndNavigate = useCallback(() => {
    setPendingChanges({});
    proceedAfterUnsavedChanges();
  }, [proceedAfterUnsavedChanges]);

  const handleSaveAndNavigate = useCallback(async () => {
    const saved = await savePendingChanges();
    if (!saved) {
      return;
    }

    proceedAfterUnsavedChanges();
  }, [proceedAfterUnsavedChanges, savePendingChanges]);

  return (
    <>
      <PageHeader title="Course Mapping">
        <nav className="course-mapping-breadcrumbs" aria-label="Breadcrumb">
          <Link to={NAVIGATION_LINKS_DATA.OVERVIEW.path}>Overview</Link>
          <span aria-hidden="true">&gt;</span>
          <span>Course Mapping</span>
        </nav>
      </PageHeader>
      <div className="main-content course-mapping-page">
        <div className="course-mapping-page-heading">
          <CourseMappingViewSelector
            presets={COURSE_MAPPING_PRESETS}
            selectedPresetId={selectedPresetId}
            unviewedPresetIds={unviewedPresetIds}
            onSelectPreset={handleSelectPreset}
          />
          <div className="course-mapping-page-description">{activePreset.description}</div>
        </div>

        <div className="course-mapping-filters">
          <CourseMappingFilters
            filters={filters}
            optionsData={{ subjectAreas, schools: schoolNames, academicPeriods }}
            lockedKeys={activePreset.filters.map((filter) => filter.key)}
            filterToOpen={filterToOpen}
            onFiltersChange={handleFiltersChange}
          />
          <CustomButton
            variant="primary"
            size="small"
            buttonText="Save changes"
            buttonAction={handleSave}
            disabled={!canSave || isSaving}
            loading={isSaving}
          />
        </div>

        <Panel className="course-mapping-panel">
          <CourseMappingTable
            groups={fetchedGroups}
            totalMatching={totalMatching}
            subjectAreas={subjectAreas}
            isPending={isPending}
            error={error}
            currentPage={currentPage}
            pendingChanges={pendingChanges}
            sort={sort}
            hiddenColumns={hiddenColumns}
            onSortChange={handleSortChange}
            onOpenFilter={handleOpenFilterForColumn}
            onHideColumn={handleHideColumn}
            onShowColumn={handleShowColumn}
            onPendingChangesChange={setPendingChanges}
            onPageChange={handlePageChange}
          />
        </Panel>
      </div>

      <CourseMappingUnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        isSaving={isSaving}
        onClose={handleCloseUnsavedChangesModal}
        onDiscard={handleDiscardAndNavigate}
        onSave={handleSaveAndNavigate}
      />
    </>
  );
};

export default CourseMappingPage;
